import React, { useEffect, useMemo, useState } from "react";
import { Link, Navigate, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, ArrowUpRight, CalendarDays, Check, MapPin, Music2, Phone, Ticket } from "lucide-react";
import { submitTourBooking } from "../services/api";
import { useTour } from "../hooks/useTours";
import "./TourBooking.css";

const baseForm = {
  organization_name: "", contact_name: "", email: "", phone: "",
  city: "", state: "", venue: "", preferred_date: "", alternate_date: "",
  estimated_attendance: "", event_type: "", message: "",
};

// Stop statuses come from tour_stops.status, managed in admin/tours.php.
const STOP_STATUS = {
  confirmed: { label: "Confirmed", Icon: Check, rowClass: "" },
  open: { label: "Available", Icon: CalendarDays, rowClass: "tour-stop-open" },
  sold_out: { label: "Sold out", Icon: Ticket, rowClass: "tour-stop-sold" },
};

const formatTourDate = (date) =>
  new Date(`${date}T12:00:00`).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
  });

export default function TourBooking() {
  const { tourSlug } = useParams();
  const { tour, tours, loading } = useTour(tourSlug);

  const initialForm = useMemo(
    () => ({ ...baseForm, artist_selection: tour?.defaultArtist ?? "" }),
    [tour]
  );

  const [form, setForm] = useState(initialForm);
  const [status, setStatus] = useState({ type: "idle", message: "" });

  useEffect(() => {
    setForm(initialForm);
    setStatus({ type: "idle", message: "" });
  }, [initialForm]);

  useEffect(() => {
    if (!tour) return;
    document.title = tour.metaTitle;
    document.querySelector('meta[name="description"]')?.setAttribute("content", tour.metaDescription);
  }, [tour]);

  // The catalogue may still be in flight — only redirect once we know the slug is unknown.
  if (!tour) return loading ? <div className="tour-page" /> : <Navigate to="/book-a-tour" replace />;

  const otherTours = tours.filter((item) => item.slug !== tour.slug);
  const bookingsOpen = tour.acceptsBookings !== false;

  const update = ({ target }) => setForm((current) => ({ ...current, [target.name]: target.value }));

  const submit = async (event) => {
    event.preventDefault();
    setStatus({ type: "loading", message: "" });
    try {
      await submitTourBooking({ ...form, tour: tour.id });
      setForm(initialForm);
      setStatus({ type: "success", message: "Your city request is in. Our tour team will contact you shortly." });
    } catch (error) {
      setStatus({ type: "error", message: error.message || "We could not send your request. Please try again." });
    }
  };

  return (
    <div className="tour-page" data-tour-theme={tour.theme}>
      <section className="tour-hero">
        <div className="tour-glow tour-glow-one" />
        <div className="tour-glow tour-glow-two" />
        <motion.div className="tour-hero-copy" initial={{ opacity: 0, y: 28 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: .8, ease: [0.22, 1, 0.36, 1] }}>
          <Link className="tour-back" to="/book-a-tour"><ArrowLeft size={15} /> All tours</Link>
          <h1>{tour.heroHeadline[0]} <em>{tour.heroHeadline[1]}</em></h1>
          <p className="tour-lede">{tour.heroBlurb}</p>
          <div className="tour-facts">
            <span><CalendarDays size={17} /> {tour.windowLabel}</span>
            <span><MapPin size={17} /> {tour.regionLabel}</span>
          </div>
          <a className="tour-primary-cta" href="#tour-request">
            Request your date <span><ArrowUpRight size={18} /></span>
          </a>
        </motion.div>

        <motion.div
          className={`tour-artists${tour.artists.length === 1 ? " tour-artists-single" : ""}`}
          initial={{ opacity: 0, x: 35 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: .14, duration: .9, ease: [0.22, 1, 0.36, 1] }}
        >
          {tour.artists.map((artist, index) => (
            <article className={`tour-artist tour-artist-${index + 1}`} key={artist.name}>
              <img src={artist.image} alt={artist.name} loading="lazy" decoding="async" />
              <div><small>{artist.role}</small><strong>{artist.name}</strong></div>
            </article>
          ))}
          <div className="tour-season-mark">
            <span>{tour.stamp.top}</span><strong>—</strong><span>{tour.stamp.bottom}</span><b>{tour.stamp.year}</b>
          </div>
        </motion.div>
      </section>

      <section className="tour-intro">
        <div>
          <h2>Your city could be the next stop.</h2>
        </div>
        <p>Tell us about your audience, venue, and preferred date. Our team will review the routing and follow up with availability, production requirements, and pricing.</p>
      </section>

      {tour.tourSchedule.length > 0 && (
        <section className="tour-booked" aria-labelledby="tour-booked-heading">
          <div className="tour-booked-heading">
            <h2 id="tour-booked-heading">Tour schedule.</h2>
            <p>Explore confirmed stops, grab tickets where they are on sale, and see the dates that are still open for booking.</p>
          </div>
          <ol className="tour-booked-list">
            {tour.tourSchedule.map((stop, index) => {
              const { label, Icon, rowClass } = STOP_STATUS[stop.status] ?? STOP_STATUS.confirmed;
              return (
                <li className={rowClass} key={stop.id ?? `${stop.date}-${stop.location}`}>
                  <span className="tour-stop-number">{String(index + 1).padStart(2, "0")}</span>
                  {stop.status === "open"
                    ? <CalendarDays aria-hidden="true" size={18} strokeWidth={1.8} />
                    : <MapPin aria-hidden="true" size={18} strokeWidth={1.8} />}
                  <span className="tour-stop-details">
                    <time dateTime={stop.date}>{formatTourDate(stop.date)}</time>
                    <strong>
                      {stop.location}
                      {stop.venue && <em className="tour-stop-venue">{stop.venue}</em>}
                    </strong>
                  </span>
                  <span className="tour-stop-actions">
                    <span className="tour-stop-status"><Icon aria-hidden="true" size={13} /> {label}</span>
                    {stop.ticketUrl && (
                      <a
                        className="tour-stop-ticket"
                        href={stop.ticketUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        aria-label={`${stop.ticketLabel || "Get tickets"} — ${stop.location}, ${formatTourDate(stop.date)}`}
                      >
                        {stop.ticketLabel || "Get tickets"} <ArrowUpRight aria-hidden="true" size={14} />
                      </a>
                    )}
                  </span>
                </li>
              );
            })}
          </ol>
        </section>
      )}

      <section className="tour-booking-wrap" id="tour-request">
        <aside className="tour-booking-aside">
          <h2>Let’s make your event unforgettable.</h2>
          <p>Submitting this form is a booking inquiry, not a confirmed reservation. Dates are held only after agreement and deposit.</p>
          <ul>
            <li><Check size={17} /> Concerts and ticketed shows</li>
            <li><Check size={17} /> Community and cultural festivals</li>
            <li><Check size={17} /> Corporate and private events</li>
          </ul>
          <div className="tour-direct">
            <Phone size={19} />
            <div>
              <small>Prefer to speak with us?</small>
              {tour.phones.map((phone) => (
                <a key={phone} href={`tel:${phone.replace(/[^+\d]/g, "")}`}>{phone}</a>
              ))}
            </div>
          </div>
        </aside>

        <div className="tour-form-shell">
          <form className="tour-form" onSubmit={submit}>
            <div className="tour-form-heading">
              <Music2 size={20} />
              <div><span>{tour.name} · {tour.edition}</span><p>Fields marked * are required.</p></div>
            </div>
            {!bookingsOpen && (
              <div className="tour-form-message error" role="status">
                Requests for this tour are closed right now — please call the tour team using the numbers on this page.
              </div>
            )}
            <div className="tour-field-grid">
              <label><span>Organization / promoter *</span><input required name="organization_name" value={form.organization_name} onChange={update} placeholder="Your organization" /></label>
              <label><span>Contact name *</span><input required name="contact_name" value={form.contact_name} onChange={update} placeholder="Full name" /></label>
              <label><span>Email address *</span><input required type="email" name="email" value={form.email} onChange={update} placeholder="you@organization.com" /></label>
              <label><span>Phone number *</span><input required type="tel" name="phone" value={form.phone} onChange={update} placeholder="+1 (000) 000-0000" /></label>
              <label><span>City *</span><input required name="city" value={form.city} onChange={update} placeholder="Type your city" /></label>
              <label><span>State *</span><input required name="state" value={form.state} onChange={update} placeholder="State" /></label>
              <label className="tour-field-wide"><span>Venue or proposed location</span><input name="venue" value={form.venue} onChange={update} placeholder="Venue name, if known" /></label>
              <label><span>Preferred date *</span><input required type="date" min={tour.dateRange.min} max={tour.dateRange.max || undefined} name="preferred_date" value={form.preferred_date} onChange={update} /></label>
              <label><span>Alternate date</span><input type="date" min={tour.dateRange.min} max={tour.dateRange.max || undefined} name="alternate_date" value={form.alternate_date} onChange={update} /></label>
              <label><span>Event type *</span><select required name="event_type" value={form.event_type} onChange={update}><option value="">Select event type</option><option>Concert</option><option>Cultural festival</option><option>Community event</option><option>Corporate event</option><option>Private event</option><option>Other</option></select></label>
              <label><span>Estimated attendance</span><input type="number" min="1" name="estimated_attendance" value={form.estimated_attendance} onChange={update} placeholder="e.g. 750" /></label>
              <label className="tour-field-wide">
                <span>Artist request *</span>
                <select required name="artist_selection" value={form.artist_selection} onChange={update}>
                  {tour.artistOptions.length === 0 && <option value="">No line-up options available</option>}
                  {tour.artistOptions.map((option) => (
                    <option key={option.value} value={option.value}>{option.label}</option>
                  ))}
                </select>
              </label>
              <label className="tour-field-wide"><span>Tell us about your event</span><textarea name="message" value={form.message} onChange={update} rows="4" placeholder="Audience, production plans, budget range, or anything else our team should know." /></label>
            </div>
            {tour.dateHint && <p className="tour-date-hint">{tour.dateHint}</p>}
            <div role="status" aria-live="polite" aria-atomic="true">
              {status.type !== "idle" && status.type !== "loading" && <div className={`tour-form-message ${status.type}`}>{status.message}</div>}
            </div>
            <button className="tour-submit" disabled={status.type === "loading" || !bookingsOpen} type="submit">
              {status.type === "loading" ? "Sending request…" : "Send booking request"} <span><ArrowUpRight size={18} /></span>
            </button>
            <p className="tour-privacy">Your details are shared only with the 3E Entertainment tour booking team.</p>
          </form>
        </div>
      </section>

      {otherTours.length > 0 && (
        <section className="tour-switch">
          <div className="tour-switch-copy">
            <h2>Looking for a different night?</h2>
          </div>
          <div className="tour-switch-links">
            {otherTours.map((item) => (
              <Link key={item.slug} to={`/book-a-tour/${item.slug}`} className="tour-switch-card">
                <img src={item.poster} alt={item.posterAlt} loading="lazy" decoding="async" />
                <div>
                  <small>{item.windowShort}</small>
                  <strong>{item.name}</strong>
                  <p>{item.lineup}</p>
                </div>
                <span className="tour-switch-arrow"><ArrowUpRight size={18} /></span>
              </Link>
            ))}
          </div>
        </section>
      )}
    </div>
  );
}
